'use client'

import PropTypes from 'prop-types'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import { useState } from 'react'
import { CopyIcon } from '@repo/ui/CopyIcon'
import { LinkIcon } from '@repo/ui/LinkIcon'
import { InfoIcon } from '@repo/ui/InfoIcon'
import { EditIcon } from '@repo/ui/EditIcon'
import { CustomDataFieldOverflowMenuItem } from '@repo/custom-data-pages/CustomDataFieldOverflowMenuItem'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'

export const CustomDataFieldOverflowMenu = ({ setShowInlineEdit, field, value }) => {
  const [copyTooltip, setCopyTooltip] = useState('Copy')
  const { allowedActions } = useMenuItemsStore()
  const allowEditCustomData = allowedActions.editCustomData?.allowed || false

  if (!field) {
    return <MUIStack data-testid="CustomDataFieldOverflowMenu" />
  }

  const copyValue = () => {
    setCopyTooltip('Copied!')
    navigator.clipboard.writeText(value)
    setTimeout(() => setCopyTooltip('Copy'), 2000)
  }

  if (value && typeof value === 'string') {
    value = value.split('\n').join(', ')
  }

  const infoTooltipContent = (
    <MUIStack direction="column" gap={5} sx={{ p: 2 }}>
      <MUIStack direction="column">
        <MUITypography variant="h6" sx={{ color: 'white', mb: 0 }}>
          FIELD
        </MUITypography>
        <MUITypography variant="body2" sx={{ color: 'white' }}>
          {field.description}
        </MUITypography>
      </MUIStack>

      <MUIStack direction="column">
        <MUITypography variant="h6" sx={{ color: 'white', mb: 0 }}>
          FIELD ID
        </MUITypography>
        <MUITypography variant="body2" sx={{ color: 'white' }}>
          {field.customDataFieldId || field.fieldId}
        </MUITypography>
      </MUIStack>

      <MUIStack direction="column">
        <MUITypography variant="h6" sx={{ color: 'white', mb: 0 }}>
          FIELD TYPE
        </MUITypography>
        <MUITypography variant="body2" sx={{ color: 'white' }}>
          {field.fieldType}
        </MUITypography>
      </MUIStack>

      <MUIStack direction="column">
        <MUITypography variant="h6" sx={{ color: 'white', mb: 0 }}>
          VALUE
        </MUITypography>
        <MUITypography variant="body2" sx={{ color: 'white' }}>
          {value}
        </MUITypography>
      </MUIStack>
    </MUIStack>
  )

  let linkIsActive = false
  let linkValue
  let hasValue = value && value !== '-'

  if (field.fieldType === 'Email' && hasValue) {
    linkIsActive = true
    linkValue = `mailto:${value}`
  }

  if (field.fieldType === 'URL' && hasValue) {
    linkIsActive = true
    linkValue = value.includes('http') ? value : `https://${value}`
  }

  return (
    <MUIStack
      data-testid="CustomDataFieldOverflowMenu"
      direction="row"
      alignItems="center"
      gap={0}
      sx={{
        position: 'absolute',
        zIndex: 1,
        height: '36px',
        top: '12px',
        right: '8px',
        p: '4px',
        borderRadius: '4px',
        backgroundColor: 'white',
        visibility: 'hidden'
      }}
    >
      <CustomDataFieldOverflowMenuItem icon={InfoIcon} tooltip={infoTooltipContent} />
      <CustomDataFieldOverflowMenuItem
        icon={CopyIcon}
        tooltip={copyTooltip}
        disabled={!hasValue}
        onClick={hasValue ? copyValue : null}
      />
      <CustomDataFieldOverflowMenuItem
        icon={LinkIcon}
        tooltip={linkIsActive ? 'Open' : ''}
        disabled={!linkIsActive}
        onClick={linkIsActive ? () => window.open(linkValue, '_blank') : null}
      />
      {allowEditCustomData ? (
        <CustomDataFieldOverflowMenuItem icon={EditIcon} tooltip="Edit" onClick={() => setShowInlineEdit(true)} />
      ) : null}
    </MUIStack>
  )
}

CustomDataFieldOverflowMenu.propTypes = {
  setShowInlineEdit: PropTypes.func,
  field: PropTypes.object,
  value: PropTypes.any,
  valueIsSet: PropTypes.bool
}
